import * as Immutable from 'immutable';
import Optimistic from 'optimistic';
import qs from 'qs';
import Reflux from 'reflux';
import _ from 'underscore';

import { IssueScopeKind, ENDPOINTS as IssuesV2Endpoints } from '../../shared/issuesV2';
import Locator from '../../shared/Locator';
import RevisionActions from '../actions/RevisionActions';
import { get } from '../redux/api';
import { nuc } from '../utils';
import type { IssueType, IssueTypesSummary, ProjectScope } from '../../shared/issuesV2';
import type { RevisionLicense } from '../../shared/licenses/common';
import type { Issue, RevisionScan } from '../types';

// TODO(leo): investigate why this store doesn't use the same error schema as others.
type RepoItem = {
  data: {
    licenses: RevisionLicense[];
    revisionScans: RevisionScan[];
    selectedRevisionScan: RevisionScan;
    latestRevisionScanId: number;
  };
  status: {
    error: {};
    stale: boolean;
  };
};

const _repos: { [key: string]: Optimistic<RepoItem> } = {};
const store = {};

function _default(id) {
  if (!_repos[id]) {
    _repos[id] = new Optimistic({
      data: {
        locator: null,
        licenses: [],
        revisionScans: [],
        selectedRevisionScan: null,
      },
      status: {
        loading: false, // consider: set default to true
        error: null, // errors propogated from API, TODO: migrate to errors[]
        stale: false,
        submitting_build: false,
      },
    });
    store[id] = _repos[id].value;
    _repos[id].on('change', (value, resolvedUpdates) => {
      const errors = _.filter(resolvedUpdates, update => !update.succeeded);
      value.status.error = errors.length > 0 ? errors[0].value.message : null;
      store[id] = processRepo(value);
      RevisionStore.trigger(store);
    });
  }
  return _repos[id];
}

function processRepo(repo: RepoItem) {
  if (repo.data) {
    // set a default selectedRevisionScan to display in ProjectSummary on first load
    if (
      repo.data.revisionScans &&
      !_.isEmpty(repo.data.revisionScans) &&
      repo.data.latestRevisionScanId &&
      !repo.data.selectedRevisionScan
    ) {
      //  If there is a revisionScanId in the query param, let's use that one
      const query = qs.parse(window.location.search.slice(1)); // `slice` removes the leading `?`
      const revisionScanId = query?.revisionScanId ? Number(query?.revisionScanId) : null;
      if (Number.isFinite(revisionScanId)) {
        repo.data.selectedRevisionScan = repo.data.revisionScans.find(revScan => revScan.id === revisionScanId) || _.head(repo.data.revisionScans);
      } else {
        repo.data.selectedRevisionScan = repo.data.revisionScans.find(revScan => revScan.id === repo.data.latestRevisionScanId) || _.head(repo.data.revisionScans);
      }
    }
  }
  return repo;
}

function repoUrl(repoId: string): string {
  return `/api/revisions/${nuc(repoId)}/`;
}

function getIssueCounts(issues: Issue[]): { [key in IssueType]: number } {
  return _
    .chain(issues)
    .reject(issue => {
      return (issue.parents && !_.isEmpty(issue.parents) && issue.parents[0]?.IssueProject?.status === 'ignored');
    })
    .groupBy('type')
    .mapObject(issueGroup => issueGroup.length)
    .value();
}

function getUnresolvedIssueCount(typeSummary: IssueTypesSummary) {
  return Object.values(typeSummary).reduce((acc, count) => acc + count, 0);
}

/* Store Definition */
const RevisionStore = Reflux.createStore({
  listenables: RevisionActions,
  // Method that publishes state update
  onUpdate(repo_id, data) {
    _default(repo_id).update(obj => obj.set('data', Immutable.fromJS(data)));
  },
  onLoadMinimal(id, silent, skipCache) {
    this.onLoad(id, silent, skipCache, true);
  },
  onLoad(id, silent, skipCache, minimal) {
    const opt_revision = _default(id);
    const callbacks = opt_revision.pushUpdate((revision, resp) => {
      const resolvedValue = resp?.data;
      revision = revision.setIn(['status', 'fullRevisionLoaded'], !minimal);
      if (resolvedValue) {
        revision = revision.set('data', Immutable.fromJS(resolvedValue)).setIn(['status', 'loading'], false);

        if (skipCache) {
          revision = revision.setIn(['status', 'stale'], false);
        }
        return revision;
      }
      return revision
        .setIn(['status', 'error'], null)
        .setIn(['status', 'loading'], silent === true ? revision.getIn(['status', 'loading']) : true);
    });
    const url = minimal ? `${repoUrl(id)}minimal` : repoUrl(id);
    const options: { url: string; headers?: {}; query?: {} } = { url };

    if (!minimal) {
      // minimal does not load steady state which is used when determining whether to rebuild the dependency locks
      options.query = {
        checkDependencyCache: true,
      };
    }

    if (skipCache || opt_revision.value.status.stale) {
      options.headers = {
        'fossa-skip-request-cache': 1,
      };
    }
    get(options.url, true, options.query, { headers: options.headers }).then(callbacks.succeeded, callbacks.failed);
  },
  onScan(repo_id: string, steady_state?: boolean) {
    const repo = _default(repo_id);
    const callbacks = repo.pushUpdate((revision, resp) => {
      const resolvedValue = resp?.data;
      revision = revision.setIn(
        ['data', 'meta'],
        Immutable.fromJS([
          {
            last_scan: Date.now(),
          },
        ]),
      );
      if (resolvedValue) { // set Issues & RevisionScan
        const unresolvedIssueCounts = getIssueCounts(resolvedValue.issues);
        const unresolvedIssueCount = getUnresolvedIssueCount(unresolvedIssueCounts);

        return revision
          .setIn(['status', 'loading'], false)
          .setIn(['status', 'stale'], true)
          .setIn(['data', 'unresolved_issue_counts'], Immutable.fromJS(unresolvedIssueCounts))
          .setIn(['data', 'unresolved_issue_count'], Immutable.fromJS(unresolvedIssueCount))
          .setIn(['data', 'selectedRevisionScan'], Immutable.fromJS(resolvedValue.revisionScan))
          .setIn(['data', 'revisionScans'], (revision.getIn(['data', 'revisionScans']) || []).unshift(Immutable.fromJS(resolvedValue.revisionScan)));
      }

      return revision.setIn(['status', 'error'], null).setIn(['status', 'loading'], true);
    });
    const q = {};
    if (steady_state) {
      q.steady = true;
    }

    return get(`${repoUrl(repo_id)}issues`, false, q).then(callbacks.succeeded, callbacks.failed);
  },

  onRebuild(repo_id, options) {
    const repo = _default(repo_id);
    const callbacks = repo.pushUpdate((revision, resp) => {
      const resolvedValue = resp?.data;
      if (resolvedValue) {
        return revision
          .setIn(['status', 'submitting_build'], false)
          .setIn(['data', 'builds'], (revision.getIn(['data', 'builds']) || []).unshift(Immutable.fromJS(resolvedValue[0])));
      }
      return revision.setIn(['status', 'submitting_build'], true);
    });
    return get(`${repoUrl(repo_id)}rebuild`, true, options || {}).then(callbacks.succeeded, callbacks.failed);
  },

  // reset the issues to the selected Scan
  onSelectRevisionScan(repoId: string, revisionScanId: number) {
    const repo = _default(repoId);
    const callbacks = repo.pushUpdate((
      revision,
      unresolvedIssueCounts: { data: IssueTypesSummary },
    ) => {
      const newRevScan = revision
        .getIn(['data', 'revisionScans'])
        .find(revScan => revScan.get('id') === revisionScanId);

      if (unresolvedIssueCounts?.data) {
        const unresolvedIssueCount = getUnresolvedIssueCount(unresolvedIssueCounts.data);

        return revision
          .setIn(['data', 'selectedRevisionScan'], newRevScan)
          .setIn(['status', 'loading'], false)
          .setIn(['status', 'error'], null)
          .setIn(['data', 'unresolved_issue_counts'], Immutable.fromJS(unresolvedIssueCounts.data))
          .setIn(['data', 'unresolved_issue_count'], Immutable.fromJS(unresolvedIssueCount));
      }

      return revision
        .setIn(['data', 'selectedRevisionScan'], newRevScan)
        .setIn(['status', 'error'], null)
        .setIn(['status', 'loading'], true);
    });

    const revisionLocator = new Locator(repoId);

    const scope: ProjectScope = {
      type: IssueScopeKind.Project,
      id: revisionLocator.toPackageString(),
      revision: revisionLocator.revision.value || '',
      revisionScanId,
    };

    return get<IssueTypesSummary>(
      IssuesV2Endpoints.TYPES_SUMMARY,
      true,
      { scope },
      { encodeQuery: true },
    ).then(callbacks.succeeded, callbacks.failed);
  },

  get(repo_id) {
    _default(repo_id);
    return store[repo_id];
  },
});
export default RevisionStore;
